import { z } from 'zod';

/**
 * User settings. Synced across devices from milestone 5 (cross-save), so the schema
 * lives in `shared`. Every field has a default: `SettingsSchema.parse({})` yields the
 * defaults, and parsing stored data from an older version fills in missing fields.
 */

export const LANGUAGES = ['it', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

export const GRAPHICS_PRESETS = ['low', 'medium', 'high', 'ultra'] as const;
export type GraphicsPreset = (typeof GRAPHICS_PRESETS)[number];

export const FPS_LIMITS = [30, 60, 120] as const;

/** Rebindable actions (spec §6). Values are `KeyboardEvent.code` strings. */
export const KEY_ACTIONS = [
  'quickPhrase1',
  'quickPhrase2',
  'quickPhrase3',
  'quickPhrase4',
  'quickPhrase5',
  'quickPhrase6',
  'quickPhrase7',
  'quickPhrase8',
  'quickPhrase9',
  'ping',
  'toggleBlueprint',
  'toggleChat',
  'pause',
] as const;
export type KeyAction = (typeof KEY_ACTIONS)[number];

export const DEFAULT_KEYBINDINGS: Record<KeyAction, string> = {
  quickPhrase1: 'Digit1',
  quickPhrase2: 'Digit2',
  quickPhrase3: 'Digit3',
  quickPhrase4: 'Digit4',
  quickPhrase5: 'Digit5',
  quickPhrase6: 'Digit6',
  quickPhrase7: 'Digit7',
  quickPhrase8: 'Digit8',
  quickPhrase9: 'Digit9',
  ping: 'Space',
  toggleBlueprint: 'Tab',
  toggleChat: 'KeyC',
  pause: 'Escape',
};

const unit = (def: number) => z.number().min(0).max(1).default(def);
const flag = (def: boolean) => z.boolean().default(def);

export const SettingsSchema = z.object({
  language: z.enum(LANGUAGES).default('en'),
  /** UI scale multiplier (spec §6: adjustable). */
  uiScale: z.number().min(0.8).max(1.5).default(1),
  audio: z
    .object({
      master: unit(0.8),
      music: unit(0.7),
      sfx: unit(0.9),
    })
    .prefault({}),
  graphics: z
    .object({
      /** `auto` picks a preset from device detection. */
      preset: z.enum(['auto', ...GRAPHICS_PRESETS]).default('auto'),
      fpsLimit: z.union([z.literal(30), z.literal(60), z.literal(120)]).default(60),
      batterySaver: flag(false),
      bloom: flag(true),
      grain: flag(true),
      chromaticAberration: flag(true),
      volumetricLight: flag(true),
      rain: flag(true),
      particles: flag(true),
    })
    .prefault({}),
  game: z
    .object({
      confirmations: flag(true),
      hints: flag(true),
      /** 0.5 = slow, 1 = normal, 2 = fast. */
      animationSpeed: z.number().min(0.5).max(2).default(1),
      /** The look of the blueprint; the ones but the classic are unlocked by playing (`@il-colpo/meta`). */
      blueprintTheme: z.enum(['classic', 'sepia', 'noir']).default('classic'),
      /** The getaway car of the escape puzzle; the ones but the sedan are unlocked by playing. */
      escapeCar: z.enum(['sedan', 'coupe', 'taxi', 'roadster']).default('sedan'),
    })
    .prefault({}),
  accessibility: z
    .object({
      colorblind: flag(false),
      largeText: flag(false),
      highContrast: flag(false),
      /** `null` means "follow the operating system". */
      reduceMotion: z.boolean().nullable().default(null),
      vibration: flag(true),
      leftHanded: flag(false),
    })
    .prefault({}),
  controls: z
    .object({
      keybindings: z
        .record(z.enum(KEY_ACTIONS), z.string())
        .default(() => ({ ...DEFAULT_KEYBINDINGS })),
    })
    .prefault({}),
  notifications: z
    .object({
      invites: flag(true),
      friends: flag(true),
      events: flag(true),
      season: flag(true),
    })
    .prefault({}),
  firstRunDone: flag(false),
});

export type Settings = z.infer<typeof SettingsSchema>;

export function defaultSettings(language: Language = 'en'): Settings {
  return SettingsSchema.parse({ language });
}
